import type {
  ClientConfig,
  ClientConfigInput,
  Duration,
  RefreshIntervals,
  RuntimeConfig,
  RuntimeConfigInput,
  TimeUnit,
} from './types'
import { defaultBranding, defaultRefresh, defaultTheme, defaultVideo } from './defaults'

/**
 * Turns a parsed `*.config.json` (a {@link ClientConfigInput}) into the runtime
 * {@link ClientConfig} the app consumes.
 *
 * The JSON is the source of truth for every field; this only does what JSON
 * itself can't:
 *  - converts human-friendly `{ value, unit }` durations into milliseconds,
 *    falling back to the bundled AccuWeather defaults when one is missing or
 *    invalid (refresh rates must never break boot),
 *  - backfills `branding`, `theme` and `video` from defaults when absent, and
 *    defaults every `features` flag to false unless explicitly true,
 *  - resolves `runtimeConfig` from the environment (a URL env var and/or the
 *    shared `VUE_APP_CDN_BASE_URL`),
 *  - validates identity + required sections and throws a clear error on those —
 *    JSON has no compile-time checking, so this is the safety net.
 *
 * Shared by the production registry (webpack `require.context`) and the Jest
 * mock (`fs`), so both paths build configs identically.
 */

const UNIT_MS: Record<TimeUnit, number> = {
  milliseconds: 1,
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
}

// Sections with no safe default — a missing one is a hard error. branding,
// theme, video and features are resilient (defaults / false) instead, below.
const REQUIRED_SECTIONS = ['domain', 'ad', 'language'] as const

/** Minimum refresh interval (ms). Matches the CDN override floor; protects TV hardware. */
const MIN_REFRESH_MS = 60_000

/** A duration → ms, or null when missing, malformed, or below the minimum. */
function toMs(d: Duration | undefined): number | null {
  if (!d || typeof d.value !== 'number' || !Number.isFinite(d.value) || !(d.unit in UNIT_MS)) {
    return null
  }
  const ms = d.value * UNIT_MS[d.unit]
  return ms >= MIN_REFRESH_MS ? ms : null
}

/** A valid interval from the JSON, else the bundled AccuWeather default. */
function intervalMs(d: Duration | undefined, fallbackMs: number, field: string): number {
  const ms = toMs(d)
  if (ms === null) {
    console.warn(`[clientConfig] ${field} missing or invalid; using default ${fallbackMs}ms`)
    return fallbackMs
  }
  return ms
}

function resolveRuntimeConfig(input: RuntimeConfigInput | undefined): RuntimeConfig {
  const useCdnBaseUrl = input?.useCdnBaseUrl ?? true
  const url = input?.urlEnvVar ? process.env[input.urlEnvVar] ?? null : input?.url ?? null
  const enabled = !!url || (useCdnBaseUrl && !!process.env.VUE_APP_CDN_BASE_URL)
  return { enabled, url, timeoutMs: input?.timeoutMs ?? 3000 }
}

export function buildClientConfig(raw: unknown, expectedId: string): ClientConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error(`Client config "${expectedId}.config.json" must be a JSON object`)
  }
  const input = raw as ClientConfigInput

  if (typeof input.clientId !== 'string' || input.clientId.length === 0) {
    throw new Error(`Client config "${expectedId}.config.json" is missing a string "clientId"`)
  }
  // The filename is the registry key, so it must match the declared clientId —
  // this keeps the production (filename) and test (also filename) keys in
  // lockstep and prevents a `?client=` resolution that works in tests but not prod.
  if (input.clientId !== expectedId) {
    throw new Error(
      `clientId "${input.clientId}" must match its filename "${expectedId}.config.json"`,
    )
  }
  for (const section of REQUIRED_SECTIONS) {
    if (typeof input[section] !== 'object' || input[section] === null) {
      throw new Error(
        `Client config "${expectedId}.config.json" is missing the "${section}" section`,
      )
    }
  }

  // Refresh rates are resilient: a missing/invalid interval falls back to the
  // bundled AccuWeather default (defaultRefresh) instead of failing the build.
  const intervals = input.refresh?.intervals
  const di = defaultRefresh.intervals
  const resolvedIntervals: RefreshIntervals = {
    currentConditionsMs: intervalMs(
      intervals?.currentConditions,
      di.currentConditionsMs,
      `${expectedId}: refresh.intervals.currentConditions`,
    ),
    hourlyForecastMs: intervalMs(
      intervals?.hourlyForecast,
      di.hourlyForecastMs,
      `${expectedId}: refresh.intervals.hourlyForecast`,
    ),
    dailyForecastMs: intervalMs(
      intervals?.dailyForecast,
      di.dailyForecastMs,
      `${expectedId}: refresh.intervals.dailyForecast`,
    ),
    mapsMs: intervalMs(intervals?.maps, di.mapsMs, `${expectedId}: refresh.intervals.maps`),
  }

  return {
    clientId: input.clientId,
    displayName: input.displayName,
    domain: input.domain,
    branding: { ...defaultBranding, ...input.branding },
    theme: { ...defaultTheme, ...input.theme },
    video: { ...defaultVideo, ...input.video },
    ad: input.ad,
    language: input.language ?? {
      defaultLanguage: 'en',
      supportedLanguages: ['en'],
      apiLanguageMap: { en: 'en' },
    },
    features: {
      showVideo: input.features?.showVideo === true,
      showLogo: input.features?.showLogo === true,
    },
    refresh: {
      enabled: input.refresh?.enabled ?? defaultRefresh.enabled,
      intervals: resolvedIntervals,
    },
    runtimeConfig: resolveRuntimeConfig(input.runtimeConfig),
  }
}
